import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';
import { PropertyType, AreaUnit, LegalRegime, MAX_OWNERS } from '../types/Property';
import type { PropertyOwner, PropertyCost } from '../types/Property';
import type { ErrorField } from '../types/ErrorField';

interface PropertyValuationInput {
    forcedLiquidationValue: number;
    valuationDate: string;
}


export default function usePropertyPageForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    // Property fields
    const [name, setName] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [registryOffice, setRegistryOffice] = useState('');
    const [registryState, setRegistryState] = useState('');
    const [propertyType, setPropertyType] = useState<PropertyType>(PropertyType.URBAN);
    const [incraCode, setIncraCode] = useState('');
    const [carCode, setCarCode] = useState('');
    const [location, setLocation] = useState('');
    const [areaValue, setAreaValue] = useState<number | ''>('');
    const [areaUnit, setAreaUnit] = useState<AreaUnit>(AreaUnit.HECTARE);
    const [legalRegime, setLegalRegime] = useState<LegalRegime>(LegalRegime.AF);
    const [appraised, setAppraised] = useState(false);
    const [monitoringEnabled, setMonitoringEnabled] = useState(false);
    const [lastMonitoringDate, setLastMonitoringDate] = useState('');
    const [comments, setComments] = useState('');
    const [description, setDescription] = useState('');

    // Obligation (optional in create)
    const [creditOperationId, setCreditOperationId] = useState('');
    const [obligations, setObligations] = useState<any[]>([]);
    const [isLoadingObligations, setIsLoadingObligations] = useState(false);

    // Owners (multiple)
    const [owners, setOwners] = useState<PropertyOwner[]>([]);

    // Owner search (candidate owner before adding to the list)
    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerResults, setOwnerResults] = useState<any[]>([]);
    const [isSearchingOwner, setIsSearchingOwner] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    // Valuation (optional)
    const [valuation, setValuation] = useState<PropertyValuationInput | null>(null);

    // Costs (optional)
    const [costs, setCosts] = useState<PropertyCost[]>([]);

    const addOwner = (owner: PropertyOwner) => {
        // Validate against the current state BEFORE calling the updater, so no
        // side effect (toast) runs inside setOwners. React 18 StrictMode can
        // invoke the functional updater more than once in dev, which would
        // fire duplicate toasts if the check lived inside it.
        if (owners.length >= MAX_OWNERS) {
            toast.error(`Máximo de ${MAX_OWNERS} proprietários permitido`);
            return;
        }
        if (owner.personId && owners.some(o => o.personId === owner.personId)) {
            toast.error('Proprietário já adicionado');
            return;
        }
        setOwners(prev => [...prev, owner]);
    };
    const removeOwner = (index: number) => setOwners(prev => prev.filter((_, i) => i !== index));

    const addCost = (cost: PropertyCost) => setCosts(prev => [...prev, cost]);
    const removeCost = (index: number) => setCosts(prev => prev.filter((_, i) => i !== index));

    // Load obligations on mount
    useEffect(() => {
        const loadObligations = async () => {
            setIsLoadingObligations(true);
            try {
                const result = await collateralApi.getObligations({ page: 1, pageSize: 500 });
                setObligations(result.items || []);
            } catch {
                setObligations([]);
            } finally {
                setIsLoadingObligations(false);
            }
        };
        loadObligations();
    }, []);

    const searchOwner = async (query: string) => {
        if (!query || query.length < 3) {
            setOwnerResults([]);
            setShowOwnerDropdown(false);
            return;
        }
        setIsSearchingOwner(true);
        try {
            const [legalResults, individualResults] = await Promise.all([
                collateralApi.searchLegalPerson(query).catch(() => []),
                collateralApi.searchPerson(query).catch(() => []),
            ]);

            const normalized = [
                ...legalResults.map((p: any) => ({
                    personId: p.personId || p.uuid || p.id || '',
                    name: p.legalName || p.fantasyName || p.name || '',
                    document: (p.govTaxId || '').replace(/\D/g, ''),
                    documentFormatted: p.govTaxIdFormatted || p.govTaxId || '',
                    type: 'PJ',
                })),
                ...individualResults.map((p: any) => ({
                    personId: p.id || p.personId || '',
                    name: p.name || p.fullName || '',
                    document: (p.socialId || '').replace(/\D/g, ''),
                    documentFormatted: p.socialId || '',
                    type: 'PF',
                })),
            ];

            setOwnerResults(normalized);
            setShowOwnerDropdown(normalized.length > 0);
        } catch {
            setOwnerResults([]);
        } finally {
            setIsSearchingOwner(false);
        }
    };

    const handleSelectOwner = (person: any) => {
        addOwner({
            personId: person.personId,
            name: person.name,
            document: person.document,
        });
        setOwnerSearch('');
        setOwnerResults([]);
        setShowOwnerDropdown(false);
    };

    const handleCreate = async () => {
        setIsCreating(true);
        setErrorFields([]);
        try {
            const isRural = propertyType === PropertyType.RURAL;
            await collateralApi.createProperty({
                registry_office: registryOffice,
                registry_state: registryState.toUpperCase(),
                property_type: propertyType,
                legal_regime: legalRegime,
                registration_number: registrationNumber ? registrationNumber : undefined,
                incra_code: isRural ? (incraCode || undefined) : undefined,
                car_code: isRural ? (carCode || undefined) : undefined,
                location: location || undefined,
                area_value: areaValue === '' ? undefined : areaValue,
                area_unit: areaValue === '' ? undefined : areaUnit,
                appraised,
                monitoring_enabled: monitoringEnabled,
                last_monitoring_date: monitoringEnabled ? (lastMonitoringDate || undefined) : undefined,
                name: name || undefined,
                description: description || undefined,
                comments: comments || undefined,
                owners: owners.map(o => ({
                    person_id: o.personId,
                    name: o.name,
                    document: o.document,
                })),
                costs: costs.map(c => ({
                    cost_type: c.costType,
                    value: c.value,
                    cost_date: c.costDate,
                    registration_number: c.registrationNumber,
                    registration_date: c.registrationDate,
                    description: c.description,
                })),
                valuation: valuation ? {
                    forced_liquidation_value: valuation.forcedLiquidationValue,
                    valuation_date: valuation.valuationDate,
                } : undefined,
                credit_operation_id: creditOperationId || undefined,
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Há campos obrigatórios!');
                setErrorFields(e.data.errors);
            } else if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
            } else {
                toast.error(e?.message || 'Erro ao cadastrar imóvel');
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        name, setName,
        registrationNumber, setRegistrationNumber,
        registryOffice, setRegistryOffice,
        registryState, setRegistryState,
        propertyType, setPropertyType,
        incraCode, setIncraCode,
        carCode, setCarCode,
        location, setLocation,
        areaValue, setAreaValue,
        areaUnit, setAreaUnit,
        legalRegime, setLegalRegime,
        appraised, setAppraised,
        monitoringEnabled, setMonitoringEnabled,
        lastMonitoringDate, setLastMonitoringDate,
        comments, setComments,
        description, setDescription,
        creditOperationId, setCreditOperationId,
        obligations, isLoadingObligations,
        owners, addOwner, removeOwner,
        ownerSearch, setOwnerSearch,
        ownerResults, isSearchingOwner, showOwnerDropdown,
        searchOwner, handleSelectOwner,
        valuation, setValuation,
        costs, addCost, removeCost,
        handleCreate,
    };
}
